import { newSecretManagerElement } from "@p0security/integrations-vaulted-credential-shared/components";
import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

import { INSTANCE_URL_HINT, toLookerBaseUrl } from "./instance-url.ts";

const allSecretManagers = newSecretManagerElement({
  owner: "Looker instance",
  connector: "Looker",
});

// The gateway reads a grant's key from Google Secret Manager only. The AWS
// option stays hidden, so the config keeps the shape the shared installer reads.
const secretManager = Element({
  ...allSecretManagers,
  default: "gcp-sm",
  options: {
    ...allSecretManagers.options,
    "aws-sm": { ...allSecretManagers.options["aws-sm"], hidden: true },
  },
});

// The connector's IAM condition matches the id up to the next `_` in each GCP
// secret name, where `/` becomes `_` and `.` becomes `-`.
const ITEM_ID_PATTERN = /^[A-Za-z0-9-]+$/;

// Google Secret Manager's rule for a secret's id. It admits no `/`, so a
// secret's resource path doesn't match.
const SECRET_ID_PATTERN = /^[A-Za-z0-9_-]{1,255}$/;

const ADMIN_KEY_SECRET_HINT =
  "Enter the secret's name, not its resource path or the key itself. Use " +
  "only letters, digits, hyphens and underscores.";

// One path segment, as in a folder's URL: /folders/<id>, at most 64 characters,
// as the connector and the Terraform provider allow.
const FOLDER_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

const CONTENT_FOLDER_ID_HINT =
  "Enter the folder's ID, the last part of its URL: /folders/<ID>";

export const LookerAiComponents = InstallSpec({
  // One item per Looker instance. Each has its own connector deployment and
  // its own Admin key.
  credential: ItemComponent({
    label: "Credential provisioning",
    description: "Allow P0 to grant agents access to Looker",
    type: "string",
    maturity: "preview",
    validator: async (id) =>
      ITEM_ID_PATTERN.test(id)
        ? undefined
        : "Use only letters, digits and hyphens",
    schema: {
      // Fixed when the item is created: a grant's Looker service account lives
      // in this instance, so another URL would be another instance.
      instanceUrl: Element({
        label: "Instance URL",
        description:
          "The instance's https://<name>.looker.app URL, with no path. P0 reaches it over the internet, so it needs a public endpoint.",
        type: "string",
        placeholder: "https://example.looker.app",
        step: "new",
        validator: async (_id, url) =>
          toLookerBaseUrl(url) ? undefined : INSTANCE_URL_HINT,
      }),
      secretManager,
      // Fixed when the item is created: the connector's deployment grants it
      // read on exactly this secret.
      adminKeySecret: Element({
        label: "Admin key secret",
        description:
          "Name of the Google Secret Manager secret that holds the API key of a Looker service account with the Admin role. Only your connector reads it. P0 never does.",
        type: "string",
        step: "new",
        validator: async (_id, name) =>
          SECRET_ID_PATTERN.test(name) ? undefined : ADMIN_KEY_SECRET_HINT,
      }),
      contentFolderId: Element({
        label: "Agent content folder ID",
        description:
          "ID of the Looker folder where agents save content, from its URL: /folders/<ID>. An edit grant can edit this folder, and content saved there outlives the grant. Without a folder, content that an agent saves goes to Trash when its grant ends.",
        type: "string",
        optional: true,
        validator: async (_id, folderId) =>
          !folderId || FOLDER_ID_PATTERN.test(folderId)
            ? undefined
            : CONTENT_FOLDER_ID_HINT,
      }),
    },
  }),
});
